'use client';
import { isTjtSettings } from '../data/publicationIdentity';
import { storedNavigationItems } from "@/lib/publications/navigation";
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { defaultTaxonomy } from '../data/taxonomy';

export default function ThemeNavigation({ siteSettings, publishedArticles = [] }) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  // Define the required core editorial categories and their taxonomy
  const taxonomy = defaultTaxonomy;

  const generalSettings = siteSettings?.general ? 
    (typeof siteSettings.general === 'string' ? JSON.parse(siteSettings.general) : siteSettings.general) 
    : {};
  const routePrefix = generalSettings.routePrefix || null;
  const buildUrl = (path) => routePrefix ? `/${routePrefix}${path}` : path;

  // Map to structured items
  let navItems = taxonomy.map(cat => ({
    label: cat.label,
    url: buildUrl(`/category/${cat.label.toLowerCase().replace(/\s+/g, '-')}`),
    children: cat.children.map(child => ({
      label: child,
      url: buildUrl(`/category/${cat.label.toLowerCase().replace(/\s+/g, '-')}/${child.toLowerCase().replace(/\s+/g, '-')}`)
    }))
  }));

  // Append any dynamic categories from articles not in taxonomy
  const dynamicCategories = Array.from(new Set(publishedArticles.map(a => a.category).filter(Boolean)));
  dynamicCategories.forEach(cat => {
    if (!navItems.find(item => item.label === cat)) {
      navItems.push({
        label: cat,
        url: buildUrl(`/category/${cat.toLowerCase().replace(/\s+/g, '-')}`),
        children: []
      });
    }
  });

  if (Array.isArray(siteSettings?.navigation?.header)) {
    navItems = storedNavigationItems(siteSettings.navigation.header, siteSettings).map(item => ({ ...item, url: item.href,
      children: item.children.map(child => ({ ...child, url: child.href })) }));
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (index, e) => {
    e.preventDefault(); // Prevent immediate navigation if clicking the main label when it has children
    setOpenDropdown(openDropdown === index ? null : index);
  };

  return (
    <nav className={`hidden md:block w-full border-b border-black py-3 mb-8 ${isTjtSettings(siteSettings) ? "border-t" : ""}`} ref={navRef}>
      <ul className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 font-sans font-bold text-[15px] md:text-[16px] tracking-wide text-black relative z-50">
        {navItems.map((item, index) => (
          <li key={index} className="relative group">
            {item.children.length > 0 ? (
              <button 
                onClick={(e) => toggleDropdown(index, e)}
                className="hover:text-gray-600 transition-colors flex items-center focus:outline-none"
              >
                {item.label}
                <svg className={`w-4 h-4 ml-1.5 fill-current opacity-80 transition-transform ${openDropdown === index ? 'rotate-180' : ''}`} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
            ) : (
              <Link href={item.url} className="hover:text-gray-600 transition-colors flex items-center">
                {item.label}
              </Link>
            )}

            {/* Dropdown Card */}
            {item.children.length > 0 && openDropdown === index && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-white border border-black shadow-lg py-3 px-4 min-w-[200px] z-50">
                <div className="flex flex-col space-y-3">
                  <Link 
                    href={item.url} 
                    className="text-[13px] font-black uppercase tracking-widest text-black hover:text-gray-600 border-b border-gray-200 pb-2 mb-1"
                    onClick={() => setOpenDropdown(null)}
                  >
                    All {item.label}
                  </Link>
                  {item.children.map((child, cIdx) => (
                    <Link 
                      key={cIdx} 
                      href={child.url} 
                      className="text-[14px] font-semibold text-gray-800 hover:text-black transition-colors"
                      onClick={() => setOpenDropdown(null)}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}


